import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { colors, fonts, spacing } from "@edufadder/ui";
import { useAuth } from "../lib/auth-context.js";
import { useRegistrationOpen } from "../lib/site.js";
import { AccountsClosedMessage } from "./AccountsClosed.js";
import { Logo } from "./Logo.js";

function LockIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 1 1 6 0v3H9Z"
      />
    </svg>
  );
}
import * as s from "../styles.js";

const publicLinks: Array<{ to: string; label: string }> = [
  { to: "/edufads", label: "Edufads" },
  { to: "/research", label: "Research" },
  { to: "/check", label: "Check a passage" },
  { to: "/get-extension", label: "Get the extension" },
  { to: "/guide", label: "How to use" },
  { to: "/about", label: "About" },
];

function navLinkStyle({ isActive }: { isActive: boolean }): React.CSSProperties {
  return {
    color: "#ffffff",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: 15,
    padding: "6px 2px",
    borderBottom: `3px solid ${isActive ? colors.gold : "transparent"}`,
  };
}

export function Layout(): JSX.Element {
  const { me, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const registrationOpen = useRegistrationOpen();
  const [lockedOpen, setLockedOpen] = useState(false);
  const isStaff = Boolean(
    me &&
      (me.permissions.includes("claims:write") ||
        me.permissions.includes("feedback:triage") ||
        me.permissions.includes("admin:users:manage")),
  );
  const orgAdminOf = me?.orgs.find((o) => o.role === "org_admin");
  const location = useLocation();

  // This site discusses edufads on purpose, so it asks the EduFadder extension not to
  // annotate its pages — except the practice page, which exists to show the extension
  // at work. (The extension honours this only on this site's own origin.)
  // The locked-sign-up note closes on navigation.
  useEffect(() => setLockedOpen(false), [location.pathname]);

  useEffect(() => {
    document.documentElement.dataset.edufadderAnnotate = location.pathname.startsWith("/practice") ? "on" : "off";
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <div style={{ ...s.page, display: "flex", flexDirection: "column" }}>
      <a href="#main" style={s.skipLink}>
        Skip to content
      </a>
      <header style={{ background: colors.brand, borderBottom: `4px solid ${colors.gold}` }}>
        <div style={s.headerInner}>
          <Link to="/" aria-label="EduFadder home" style={{ textDecoration: "none" }}>
            <Logo size={36} onDark />
          </Link>
          <button
            type="button"
            className="ef-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="ef-nav"
            onClick={() => setMenuOpen((v) => !v)}
            style={s.menuToggle}
          >
            Menu
          </button>
          <nav id="ef-nav" aria-label="Main" className={menuOpen ? "ef-nav ef-nav-open" : "ef-nav"} style={s.navBar}>
            {publicLinks.map((l) => (
              <NavLink key={l.to} to={l.to} style={navLinkStyle} onClick={() => setMenuOpen(false)}>
                {l.label}
              </NavLink>
            ))}
            <span style={{ flex: 1 }} />
            {me ? (
              <>
                <NavLink to="/dashboard" style={navLinkStyle} onClick={() => setMenuOpen(false)}>
                  My dashboard
                </NavLink>
                <button type="button" style={s.buttonOnDark} onClick={() => void logout()}>
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" style={navLinkStyle} onClick={() => setMenuOpen(false)}>
                  Sign in
                </NavLink>
                {registrationOpen ? (
                  <Link to="/register" style={s.buttonGold} onClick={() => setMenuOpen(false)}>
                    Create free account
                  </Link>
                ) : (
                  <span style={{ position: "relative", display: "inline-block" }}>
                    <button
                      type="button"
                      aria-expanded={lockedOpen}
                      aria-controls="accounts-closed-note"
                      title="Account creation is temporarily locked"
                      onClick={() => setLockedOpen((v) => !v)}
                      style={{ ...s.buttonGold, opacity: 0.75, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <LockIcon />
                      Create free account
                    </button>
                    {lockedOpen && (
                      <span
                        id="accounts-closed-note"
                        role="status"
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "calc(100% + 8px)",
                          width: 300,
                          maxWidth: "80vw",
                          background: colors.surface,
                          color: colors.ink,
                          border: `1px solid ${colors.border}`,
                          borderTop: `4px solid ${colors.gold}`,
                          borderRadius: 10,
                          padding: "12px 14px",
                          fontSize: 14,
                          lineHeight: 1.5,
                          fontWeight: 400,
                          boxShadow: "0 12px 28px rgba(16,24,32,0.25)",
                          zIndex: 50,
                          textAlign: "left",
                        }}
                      >
                        <AccountsClosedMessage />
                      </span>
                    )}
                  </span>
                )}
              </>
            )}
          </nav>
        </div>
      </header>

      {isStaff && (
        <div style={{ background: colors.goldTint, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ ...s.headerInner, padding: `${spacing(2)} ${spacing(4)}`, gap: spacing(4), fontSize: 14, flexWrap: "wrap" }}>
            <strong style={{ color: colors.brand }}>Workbench:</strong>
            {me?.permissions.includes("claims:write") && <Link to="/claims">Claims &amp; evidence</Link>}
            {me?.permissions.includes("feedback:triage") && <Link to="/feedback">Feedback</Link>}
            {me?.permissions.includes("feedback:triage") && <Link to="/suggestions">Suggestions</Link>}
            {me?.permissions.includes("claims:read") && <Link to="/releases">Releases</Link>}
            {me?.permissions.includes("admin:users:manage") && <Link to="/admin">Admin &amp; insights</Link>}
            {orgAdminOf && <Link to={`/orgs/${orgAdminOf.id}`}>My organization</Link>}
          </div>
        </div>
      )}
      {!isStaff && orgAdminOf && (
        <div style={{ background: colors.goldTint, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ ...s.headerInner, padding: `${spacing(2)} ${spacing(4)}`, fontSize: 14 }}>
            <Link to={`/orgs/${orgAdminOf.id}`}>My organization</Link>
          </div>
        </div>
      )}

      <main id="main" style={{ flex: 1 }}>
        <Outlet />
      </main>

      <footer style={{ background: colors.ink, color: "#e8e6df", marginTop: spacing(10) }}>
        <div style={{ ...s.container, paddingTop: spacing(8), paddingBottom: spacing(8) }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: spacing(8), justifyContent: "space-between" }}>
            <div style={{ maxWidth: 420 }}>
              <Logo size={28} onDark />
              <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: spacing(3) }}>
                Created by <Link to="/about#authors" style={{ color: colors.gold }}>Dr. Kadir Kozan</Link> and{" "}
                <Link to="/about#authors" style={{ color: colors.gold }}>Jean Baptiste Mbanzabugabo</Link>, Instructional
                Systems &amp; Learning Technologies, Florida State University.
              </p>
              <p style={{ fontSize: 12, color: "#b9b6ad", lineHeight: 1.5 }}>
                Developed at Florida State University. EduFadder is a research project in progress, not an official
                software product or service of Florida State University.
              </p>
            </div>
            <nav aria-label="Footer" style={{ display: "flex", flexDirection: "column", gap: spacing(2), fontSize: 14 }}>
              <strong style={{ fontFamily: fonts.serif, color: colors.gold }}>EduFadder</strong>
              <Link to="/edufads" style={s.footerLink}>Edufads library</Link>
              <Link to="/check" style={s.footerLink}>Check a passage</Link>
              <Link to="/research" style={s.footerLink}>Find research</Link>
              <Link to="/get-extension" style={s.footerLink}>Get the extension</Link>
              <Link to="/guide" style={s.footerLink}>How to use</Link>
              <Link to="/suggest" style={s.footerLink}>Suggest an edufad</Link>
            </nav>
            <nav aria-label="Project" style={{ display: "flex", flexDirection: "column", gap: spacing(2), fontSize: 14 }}>
              <strong style={{ fontFamily: fonts.serif, color: colors.gold }}>Project</strong>
              <Link to="/about" style={s.footerLink}>About &amp; authors</Link>
              <Link to="/privacy" style={s.footerLink}>Privacy</Link>
              <Link to="/about#cite" style={s.footerLink}>How to cite</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
